import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import {
    Menu,
    Search,
    Instagram,
    Linkedin,
    AtSign,
    Triangle,
    Cloud,
} from "lucide-react";
import { LOGO_URL, NAV_LINKS, BRAND } from "@/lib/constants";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetHeader,
    SheetTitle,
} from "@/components/ui/sheet";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
    DialogDescription,
} from "@/components/ui/dialog";
import {
    Accordion,
    AccordionItem,
    AccordionTrigger,
    AccordionContent,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { subscribe } from "@/lib/api";

const SOCIAL_ICONS = {
    Instagram,
    Linkedin,
    AtSign,
    Triangle,
    Cloud,
};

function slugify(s) {
    return s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
}

export default function Header() {
    const navigate = useNavigate();
    const [search, setSearch] = useState("");
    const [email, setEmail] = useState("");
    const [website, setWebsite] = useState(""); // honeypot
    const [subOpen, setSubOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const subRenderedAtRef = useRef(Date.now());

    useEffect(() => {
        if (subOpen) subRenderedAtRef.current = Date.now();
    }, [subOpen]);

    const submitSearch = (e) => {
        e.preventDefault();
        const q = search.trim();
        if (!q) return;
        navigate(`/search?q=${encodeURIComponent(q)}`);
        setSearch("");
    };

    const handleSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        try {
            await subscribe({
                email,
                website_url: website,
                rendered_at: subRenderedAtRef.current,
            });
            toast.success("Welcome to BENNE.", {
                description: "You're on the list.",
            });
            setEmail("");
            setSubOpen(false);
        } catch (err) {
            if (err?.response?.status === 429) {
                toast.error("Too many attempts. Try again in 15 minutes.");
            } else {
                toast.error("Subscription failed. Try again.");
            }
        }
    };

    return (
        <header
            className="sticky top-0 z-40 bg-[#fcbf49] text-[#1B1B1B]"
            data-testid="site-header"
        >
            <div className="px-4 md:px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
                {/* Title logo (left) */}
                <Link
                    to="/"
                    className="flex items-center group shrink-0"
                    data-testid="header-logo-link"
                >
                    <img
                        src={LOGO_URL}
                        alt="BENNE"
                        className="h-10 md:h-12 w-auto object-contain"
                    />
                </Link>

                {/* Right utilities */}
                <div className="flex items-center gap-3 md:gap-5">
                    {/* Submit a Press Release CTA (capsule) */}
                    <a
                        href="https://bennebrandsmedia.com/submit-press-release"
                        className="hidden sm:inline-flex items-center font-poppins font-light text-[0.78rem] tracking-[0.14em] uppercase rounded-full px-5 py-2 transition-colors"
                        style={{
                            backgroundColor: "#f73b27",
                            color: "#fff",
                            fontWeight: 300,
                        }}
                        onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#201638")
                        }
                        onMouseLeave={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f73b27")
                        }
                        data-testid="submit-press-release-cta"
                    >
                        Submit a Press Release
                    </a>

                    {/* Hamburger (always present) */}
                    <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                        <SheetTrigger asChild>
                            <button
                                className="p-1.5 hover:opacity-70 transition flex items-center gap-2"
                                aria-label="Open menu"
                                data-testid="hamburger-menu-button"
                            >
                                <Menu className="h-6 w-6" />
                                <span className="hidden md:inline font-poppins text-[0.78rem] tracking-[0.18em] uppercase">
                                    Navigation
                                </span>
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="left"
                            className="bg-[#1B1B1B] text-white border-none w-[340px] overflow-y-auto"
                            data-testid="hamburger-menu-panel"
                        >
                            <SheetHeader>
                                <SheetTitle className="text-white font-poppins text-2xl">
                                    Navigation
                                </SheetTitle>
                            </SheetHeader>
                            <nav className="mt-6 flex flex-col">
                                {NAV_LINKS.map((item) =>
                                    item.children ? (
                                        <Accordion
                                            type="single"
                                            collapsible
                                            key={item.to}
                                            className="border-b border-white/10"
                                        >
                                            <AccordionItem
                                                value={item.to}
                                                className="border-none"
                                            >
                                                <AccordionTrigger
                                                    className="font-poppins text-lg py-3 hover:text-[#F5C53D] hover:no-underline"
                                                    data-testid={`mobile-nav-${slugify(item.label)}-trigger`}
                                                >
                                                    {item.label}
                                                </AccordionTrigger>
                                                <AccordionContent className="pb-3 pl-3">
                                                    <NavLink
                                                        to={item.to}
                                                        end
                                                        onClick={() => setMenuOpen(false)}
                                                        className="block py-2 font-sofia text-sm text-white/70 hover:text-[#F5C53D]"
                                                        data-testid={`mobile-nav-${slugify(item.label)}-overview`}
                                                    >
                                                        Overview
                                                    </NavLink>
                                                    {item.children.map((c) => (
                                                        <NavLink
                                                            key={c.to}
                                                            to={c.to}
                                                            onClick={() => setMenuOpen(false)}
                                                            className={({ isActive }) =>
                                                                `block py-2 font-sofia text-sm transition ${
                                                                    isActive
                                                                        ? "text-[#F5C53D]"
                                                                        : "text-white/85 hover:text-[#F5C53D]"
                                                                }`
                                                            }
                                                            data-testid={`mobile-nav-${slugify(c.label)}`}
                                                        >
                                                            {c.label}
                                                        </NavLink>
                                                    ))}
                                                </AccordionContent>
                                            </AccordionItem>
                                        </Accordion>
                                    ) : (
                                        <NavLink
                                            key={item.to}
                                            to={item.to}
                                            end={item.to === "/"}
                                            onClick={() => setMenuOpen(false)}
                                            className={({ isActive }) =>
                                                `font-poppins text-lg py-3 border-b border-white/10 transition ${
                                                    isActive
                                                        ? "text-[#F5C53D]"
                                                        : "text-white hover:text-[#F5C53D]"
                                                }`
                                            }
                                            data-testid={`mobile-nav-${slugify(item.label)}`}
                                        >
                                            {item.label}
                                        </NavLink>
                                    ),
                                )}
                            </nav>
                            <div className="mt-8 flex gap-4">
                                {BRAND.socials.map((s) => {
                                    const Icon = SOCIAL_ICONS[s.icon];
                                    return (
                                        <a
                                            key={s.name}
                                            href={s.href}
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-label={s.name}
                                            className="hover:text-[#F5C53D] transition"
                                            data-testid={`mobile-social-${s.name.toLowerCase()}`}
                                        >
                                            <Icon className="h-5 w-5" />
                                        </a>
                                    );
                                })}
                            </div>
                        </SheetContent>
                    </Sheet>

                    {/* Subscribe (kept as primary CTA) */}
                    <Dialog open={subOpen} onOpenChange={setSubOpen}>
                        <DialogTrigger asChild>
                            <button
                                className="font-poppins text-[0.78rem] tracking-[0.18em] uppercase hover:text-[#E63B26] transition"
                                data-testid="subscribe-button"
                            >
                                Subscribe
                            </button>
                        </DialogTrigger>
                        <DialogContent
                            className="bg-white text-[#1B1B1B] sm:max-w-md"
                            data-testid="subscribe-dialog"
                        >
                            <DialogHeader>
                                <DialogTitle className="font-poppins text-3xl text-[#E63B26]">
                                    Join the BENNE list
                                </DialogTitle>
                                <DialogDescription className="font-sofia text-base">
                                    Monthly drops, openings, opportunities and
                                    interviews — straight to your inbox.
                                </DialogDescription>
                            </DialogHeader>
                            <form
                                onSubmit={handleSubscribe}
                                className="mt-4 flex flex-col gap-3"
                            >
                                {/* honeypot — must remain empty */}
                                <input
                                    type="text"
                                    name="website_url"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                    aria-hidden="true"
                                    style={{
                                        position: "absolute",
                                        left: "-10000px",
                                        width: "1px",
                                        height: "1px",
                                        opacity: 0,
                                    }}
                                    data-testid="header-subscribe-honeypot"
                                />
                                <Input
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="font-sofia"
                                    data-testid="subscribe-email-input"
                                />
                                <Button
                                    type="submit"
                                    className="bg-[#E63B26] hover:bg-[#1B1B1B] text-white font-poppins tracking-[0.18em] uppercase rounded-none h-12"
                                    data-testid="subscribe-submit-button"
                                >
                                    Subscribe
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>

                    {/* Search */}
                    <form
                        onSubmit={submitSearch}
                        className="hidden sm:flex items-center gap-2 bg-white text-[#1B1B1B] px-3 py-2 rounded-full w-44 md:w-56 lg:w-64"
                        data-testid="header-search-form"
                        role="search"
                    >
                        <button
                            type="submit"
                            aria-label="Search"
                            className="text-[#1B1B1B]/60 hover:text-[#E63B26] transition"
                            data-testid="header-search-submit"
                        >
                            <Search className="h-4 w-4" />
                        </button>
                        <input
                            type="text"
                            placeholder="Search"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="bg-transparent outline-none w-full text-sm font-sofia"
                            data-testid="header-search-input"
                        />
                    </form>

                    {/* Social icons */}
                    <div className="hidden lg:flex items-center gap-3 shrink-0">
                        {BRAND.socials.map((s) => {
                            const Icon = SOCIAL_ICONS[s.icon];
                            return (
                                <a
                                    key={s.name}
                                    href={s.href}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={s.name}
                                    className="hover:text-[#E63B26] transition"
                                    data-testid={`header-social-${s.name.toLowerCase()}`}
                                >
                                    <Icon className="h-4 w-4" />
                                </a>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Yellow brand strip */}
            <div className="marquee-strip text-center px-4 py-1.5">
                Amplifying Underrepresented Voices in Food &amp; Hospitality
            </div>
        </header>
    );
}
